import { Alert, Link, Typography } from '@mui/material';
import { StyledSimpleCard } from 'styles';
import { getEnvVar } from 'utils/env';
import GrafanaPanel from 'GrafanaModule/components/GrafanaPanel';

/**
 * "Realm Migrations — History", panel 5: one row per attempt.
 *
 * Pinned by id with the title recorded here because panel ids are UI-assigned and there is
 * no dashboards-as-code path, so a rebuilt panel renumbers silently and the only way to
 * diagnose a suddenly-wrong embed is to know which panel it was meant to be.
 */
const DASHBOARD_UID = 'realm-migrations-history';
const PANEL_ID = 5;
const GRAFANA_URL = 'https://grafana.prod.us-east4.mosaicapp.com';
const RANGE_FROM = 'now-30d';

/**
 * Deep link to the same view the snapshot shows.
 *
 * The datasource has to be pinned here for exactly the reason it is pinned on the embed:
 * a bare `/d/<uid>` opens against the dashboard's saved current datasource, which is Prod,
 * so the "open in Grafana" link out of the party UI would show production migrations. The
 * range is carried too, so the interactive view matches the image it sits under.
 */
const deepLink = (datasource: string) =>
  `${GRAFANA_URL}/d/${DASHBOARD_UID}?${new URLSearchParams({
    'var-galaxy': datasource,
    from: RANGE_FROM,
    to: 'now',
  }).toString()}`;

/**
 * Maps the UI environment onto the Grafana datasource for this environment's galaxy DB.
 *
 * This is the interim of spec item B.7: the history dashboard has no `env` template
 * variable to chain the datasource regex off, so the datasource has to be named
 * explicitly. It cannot be defaulted or omitted — Grafana would fall back to the
 * dashboard's SAVED current, which is Prod, and the party UI would silently render
 * production migration data. Once the dashboard gains an `env` variable this collapses
 * into the `UI_ENV` Title-Case idiom `RecentLoginsTab` already uses.
 */
const galaxyDatasourceForEnv = (uiEnv: string) => {
  switch (uiEnv.toLowerCase()) {
    case 'prod':
      return 'Prod galaxy-server';
    case 'party':
      return 'Party galaxy-server';
    default:
      return undefined;
  }
};

export const MigrationDashboardCard = () => {
  const datasource = galaxyDatasourceForEnv(getEnvVar('UI_ENV') || '');
  // No datasource means no honest link to offer — the panel is disabled in that case too.
  const link = datasource ? deepLink(datasource) : null;

  return (
    <StyledSimpleCard>
      <h3>Migration History (Grafana)</h3>

      <GrafanaPanel
        dashboardUid={DASHBOARD_UID}
        panelId={PANEL_ID}
        variables={datasource ? { galaxy: datasource } : undefined}
        width={1400}
        height={420}
        // 30 days, not the dashboard's saved 90: at 90 this panel intermittently exceeds
        // the renderer's own ~33s timeout and comes back as a 500.
        from={RANGE_FROM}
        to="now"
        // The image renderer is a single replica. React-query's default three retries turn
        // one slow render into four concurrent ones against the thing that was already too
        // slow, and give the user a two-minute spinner before the error.
        retry={1}
        retryDelay={35_000}
        alt="Realm migration history"
        missingContext={
          <Typography color="text.secondary">
            No Grafana datasource is configured for this environment.
          </Typography>
        }
        empty={<Typography color="text.secondary">No migrations in the last 30 days.</Typography>}
        renderError={() => (
          <Alert severity="warning">
            This panel is taking too long to render.{' '}
            {link && (
              <Link href={link} target="_blank" rel="noopener noreferrer">
                Open it in Grafana
              </Link>
            )}
          </Alert>
        )}
      >
        <Typography variant="caption" color="text.secondary">
          Rendered snapshot.{' '}
          {link && (
            <Link href={link} target="_blank" rel="noopener noreferrer">
              Open in Grafana
            </Link>
          )}{' '}
          for the interactive view.
        </Typography>
      </GrafanaPanel>
    </StyledSimpleCard>
  );
};

export default MigrationDashboardCard;
